import { useState } from 'react'
import { CalendarBlank, SmileyWink } from 'phosphor-react'

import { Text } from '../Text'
import { Column, Row } from '../Grid'
import PatientType from './PatientType'
import { NextImage } from '../NextImage'
import { BookmarkButton } from '../Favorite'
import PsychoPrice from '../PsychoPrice/PsychoPrice'
import ConsultationDuration from './ConsultationDuration'
import { UnavailablePsyMessage } from './UnavailablePsyMessage'
import Evaluation from 'components/Search/Results/Card/Intro/evaluation'

const ProfileData = ({ psycho, schedules, isPsyInPartnership }) => {
  const [isExpanded, setExpanded] = useState(false)
  const [isAboutMeExpanded, setAboutMeExpanded] = useState(false)
  const skills = [...new Set(psycho.main_skills.concat(psycho?.minor_skills || []))]

  return (
    <Row flexDirection='column' width='100%' mt='24px' gridGap='8px'>
      <Text variant='regular' lineHeight='24px' textAlign='left'>
        {`Psicólog${psycho.gender === 'F' ? 'a' : 'o'}`}
      </Text>
      <Row>
        <Row alignItems='center' flexGrow='1' gridGap='8px'>
          <Text
            as='h1'
            mr='10px'
            fontSize={['24px', '24px', '32px']}
            lineHeight='32px'
            itemProp='name'
            textAlign='left'
            width='80%'
          >
            {psycho.name}
          </Text>
          {psycho.number_of_consultations >= 10 && (
            <NextImage
              src='/static/images/verified.png'
              alt='Psicólogo Verificado'
              width={14}
              height={14}
            />
          )}
        </Row>

        <BookmarkButton
          isLiked={psycho.is_liked}
          professionalId={psycho.id}
          professionalSlug={psycho.slug}
        />
      </Row>

      <Text variant='regular' textAlign='left'>
        CRP {psycho.prof_register}
      </Text>

      <Evaluation psycho={psycho} numbertotal={psycho.rating.total} isAggregatedReview />

      <Row flexWrap='wrap' mt='16px'>
        {psycho.symptoms.map((symptom, index) => (
          <Column
            key={`${symptom}${index}`}
            color='#293450'
            p='4px 8px'
            mb='8px'
            borderRadius='8px'
            border='1px solid'
            borderColor='#E7EAF4'
            fontSize='14px'
            lineHeight='24px'
            mr='4px'
          >
            {symptom}
          </Column>
        ))}
      </Row>

      {!!skills?.length && (
        <Row flexWrap='wrap'>
          <Text textAlign='left' color='#b9b9b9' fontSize='16px' fontWeight='400' lineHeight='24px'>
            {skills.join(', ')}
          </Text>
        </Row>
      )}

      <Row
        width='100%'
        justifyContent='center'
        mt='20px'
        flexDirection={['row', 'row', 'column']}
        gridGap='16px'
        alignItems='center'
        borderRadius={['8px', '8px', '0px']}
        display={['flex', 'flex', 'none']}
      >
        {isPsyInPartnership ? (
          <>
            <PsychoPrice
              originalPrice={schedules?.price?.value}
              contractPrice={schedules?.contract_price?.value}
              showPaymentMethodMessage
            />
            <ConsultationDuration />
            <PatientType psycho={psycho} />
          </>
        ) : (
          <UnavailablePsyMessage />
        )}
      </Row>

      <Column textAlign='left' gridGap='8px' width='100%' maxWidth={['320px', '320px', '100%']}>
        <Text variant='regular' as='h3' my='16px' fontWeight='600'>
          FORMAÇÃO ACADÊMICA
        </Text>
        {!!psycho?.professional_education?.length ? (
          <>
            {psycho?.professional_education?.map(
              (entry, index) =>
                (index < 2 || isExpanded) && (
                  <Column color='#313a4c' pl='8px' key={`${entry.degree}${index}`}>
                    <Text as='span' fontWeight='600' color='#313a4c'>
                      {entry.grade} - {entry.degree}
                    </Text>
                    <Text as='span' fontWeight='300' color='#313a4c'>
                      {entry.school}
                    </Text>
                    <Text as='span' fontWeight='300' color='#313a4c'>
                      Período: {entry.start_year} - {entry.end_year}
                    </Text>
                    <Text as='span' fontWeight='300' color='#313a4c'>
                      {entry.field_of_study}
                    </Text>
                    <Text as='span' fontWeight='300' color='#313a4c'>
                      {entry.description}
                    </Text>
                  </Column>
                )
            )}
            {!isExpanded && psycho?.professional_education?.length > 1 && (
              <Text
                cursor='pointer'
                onClick={() => setExpanded(true)}
                fontWeight='400'
                mt='16px'
                color='#FF7708'
                as='span'
              >
                Ver formação acadêmica completa.
              </Text>
            )}
          </>
        ) : (
          <div>
            <p>{psycho?.academic_education}</p>
          </div>
        )}
        <Text variant='regular' as='h3' my='16px' fontWeight='600'>
          SOBRE MIM
        </Text>
        <Text
          as='span'
          lineHeight='24px'
          color='#313a4c'
          fontWeight='300'
          width='100%'
          maxWidth={['320px', '320px', '100%']}
          style={{
            whiteSpace: 'pre-wrap'
          }}
        >
          {isAboutMeExpanded ? psycho?.mini_cv : psycho?.mini_cv.slice(0, 250)}
          {psycho?.mini_cv.length > 250 && !isAboutMeExpanded ? ' (...) ' : ' '}
          <Text
            cursor='pointer'
            onClick={() => setAboutMeExpanded(prev => !prev)}
            fontWeight='400'
            my='16px'
            color='#FF7708'
            as='span'
          >
            {isAboutMeExpanded ? 'Ler menos' : 'Ler mais'}
          </Text>
        </Text>

        {!!psycho?.languages.length && (
          <Text variant='small' color='#888' fontWeight='600' lineHeight='21px'>
            Atendimento também em{' '}
            <Text fontWeight='300' as='span' variant='small' color='#757474'>
              {psycho?.languages.join(' / ')}
            </Text>
          </Text>
        )}

        <Row justifyContent='space-evenly' mt='16px' width='100%'>
          <Column>
            <Row mb='4px' alignItems='center' justifyContent='flex-start'>
              <Text color='primary' fontSize='14px' fontWeight='bold' textAlign='left'>
                {psycho.delta_joined.years !== 0 ? (
                  <Text color='primary' fontSize='14px' fontWeight='bold'>
                    <CalendarBlank size={18} />{' '}
                    {psycho.delta_joined.years === 1
                      ? `${psycho.delta_joined.years} ano`
                      : `${psycho.delta_joined.years} anos`}
                    {psycho.delta_joined.months > 0 &&
                      (psycho.delta_joined.months > 1
                        ? `, ${psycho.delta_joined.months} meses `
                        : `, ${psycho.delta_joined.months} mês `)}
                  </Text>
                ) : (
                  <Text color='primary' fontSize='14px' fontWeight='bold'>
                    <CalendarBlank size={18} />
                    {psycho.delta_joined.months <= 1
                      ? 'Novo'
                      : `${psycho.delta_joined.months} meses`}
                  </Text>
                )}
                <Text color='#999999' fontSize='14px'>
                  na Vittude
                </Text>
              </Text>
            </Row>
          </Column>
          <Column>
            <Row mb='4px' alignItems='center' justifyContent='flex-start'>
              <SmileyWink size={16} color='#FF7708' />
              <Text as='span' variant='small' color='primary' fontWeight='600' ml='4px'>
                {psycho?.number_of_consultations} consulta
                {psycho?.number_of_consultations > 1 && 's'}
              </Text>
            </Row>
            <Text as='span' color='#757474' variant='small' fontWeight='300'>
              na Vittude
            </Text>
          </Column>
        </Row>
      </Column>
    </Row>
  )
}

export { ProfileData }
